import { useParams, useSearchParams } from 'react-router-dom';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useSharedPageContext } from '../../contexts/SharedPageContext';
import { SharedPage } from '../SharedPage/SharedPage';
import { AgentComplianceReviewDesktop } from './AgentComplianceReviewDesktop';
import { useAgentComplianceReviewOverview } from './hooks/useAgentComplianceReviewOverview';

/**
 * Shell for the public, no-login agent compliance review page. Loads the
 * overview once the share-link token is verified, then delegates rendering to
 * the layout component. When a document is selected (the `?documentId=` query
 * param) the selected id flows down and the layout lazily loads that document's
 * detail.
 *
 * Kept intentionally thin: data/verification gating lives here, the three-pane
 * assembly lives in {@link AgentComplianceReviewDesktop}.
 */
const AgentComplianceReviewContent = () => {
  const { isShareLinkVerified } = useSharedPageContext();
  const [searchParams] = useSearchParams();
  const selectedDocumentId = searchParams.get('documentId') ?? undefined;

  const { overview, loading, token, ingestionFolderUuid } =
    useAgentComplianceReviewOverview();

  if (!isShareLinkVerified || loading) {
    return <CenterSpinner />;
  }

  if (!overview) {
    return null;
  }

  return (
    <AgentComplianceReviewDesktop
      ingestionFolderUuid={ingestionFolderUuid}
      overview={overview}
      selectedDocumentId={selectedDocumentId}
      token={token}
    />
  );
};

/**
 * Route-level wrapper. Mounts {@link SharedPage} once (binding verification to
 * the folder) and renders the review content inside it, so the content inherits
 * SharedPage's verification + expired-link handling. The open document is view
 * state in the query string (`?documentId=`), so there are no child routes.
 */
export const AgentComplianceReviewPage = () => {
  const { ingestionFolderUuid } = useParams();

  return (
    <SharedPage ingestionFolderUuid={ingestionFolderUuid}>
      <AgentComplianceReviewContent />
    </SharedPage>
  );
};
